import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { api } from '../api';
import { defineZenComponent } from './register';
import {
  normalizeWorkspaceSyncRules,
  workspaceSyncRulesEqual,
  workspaceSyncRulesPatch,
  type NoDueDateMode,
  type WorkspaceSyncRulesDraft,
} from '../workspace-sync-rules-logic';

type WorkspaceViewerRole = 'owner' | 'admin' | 'member' | 'guest';

const DEFAULT_DRAFT: WorkspaceSyncRulesDraft = {
  windowDays: 7,
  excludeAllDay: false,
  noDueDate: 'keep_empty',
};

function errorMessage(): string {
  return t('workspaceSyncRulesGenericError') as string;
}

export class ZenWorkspaceSyncRulesSection extends LitElement {
  static properties = {
    loading: { state: true },
    loaded: { state: true },
    saving: { state: true },
    error: { state: true },
    success: { state: true },
    readbackFailed: { state: true },
    viewerRole: { state: true },
    draft: { state: true },
    baseline: { state: true },
  };

  declare loading: boolean;
  declare loaded: boolean;
  declare saving: boolean;
  declare error: string;
  declare success: string;
  declare readbackFailed: boolean;
  declare viewerRole: WorkspaceViewerRole;
  declare draft: WorkspaceSyncRulesDraft;
  declare baseline: WorkspaceSyncRulesDraft;

  private requestGeneration = 0;
  private dirtyNotified = false;

  constructor() {
    super();
    this.loading = false;
    this.loaded = false;
    this.saving = false;
    this.error = '';
    this.success = '';
    this.readbackFailed = false;
    this.viewerRole = 'member';
    this.draft = { ...DEFAULT_DRAFT };
    this.baseline = { ...DEFAULT_DRAFT };
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('zen-lang-change', this.onLangChange);
    document.addEventListener('workspace-settings-discard', this.onDiscardRequest);
    void this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('zen-lang-change', this.onLangChange);
    document.removeEventListener('workspace-settings-discard', this.onDiscardRequest);
    this.requestGeneration += 1;
    this.emitDirtyState(false);
    super.disconnectedCallback();
  }

  private onLangChange = () => this.requestUpdate();

  private onDiscardRequest = () => this.discardChanges();

  private get isOwner(): boolean {
    return this.viewerRole === 'owner';
  }

  private get dirty(): boolean {
    return !workspaceSyncRulesEqual(this.draft, this.baseline);
  }

  private isCurrent(generation: number): boolean {
    return generation === this.requestGeneration && this.isConnected;
  }

  private emitDirtyState(dirty: boolean) {
    if (dirty === this.dirtyNotified) return;
    this.dirtyNotified = dirty;
    this.dispatchEvent(new CustomEvent('workspace-settings-dirty-change', {
      bubbles: true,
      composed: true,
      detail: { dirty },
    }));
  }

  private setDraft(next: WorkspaceSyncRulesDraft) {
    this.draft = next;
    this.error = '';
    this.success = '';
    this.readbackFailed = false;
    this.emitDirtyState(!workspaceSyncRulesEqual(next, this.baseline));
  }

  private extractSyncRules(data: any): unknown {
    return data?.settings?.sync_rules ?? data?.workspace?.sync_rules ?? {};
  }

  private async load() {
    const generation = ++this.requestGeneration;
    this.loading = true;
    this.error = '';
    this.success = '';
    this.readbackFailed = false;
    try {
      const data = await api('/api/workspaces/me');
      if (!this.isCurrent(generation)) return;
      const role = data?.viewer_membership?.role;
      this.viewerRole = role === 'owner' || role === 'admin' || role === 'guest' ? role : 'member';
      const next = normalizeWorkspaceSyncRules(this.extractSyncRules(data));
      this.baseline = next;
      this.draft = { ...next };
      this.loaded = true;
      this.emitDirtyState(false);
    } catch {
      if (!this.isCurrent(generation)) return;
      this.error = t('workspaceSyncRulesLoadError') as string;
    } finally {
      if (this.isCurrent(generation)) this.loading = false;
    }
  }

  private retryLoad() {
    if (this.saving) return;
    void this.load();
  }

  /** Called by the settings shell after the user confirms leaving with a dirty draft. */
  discardChanges() {
    this.requestGeneration += 1;
    this.draft = { ...this.baseline };
    this.error = '';
    this.success = '';
    this.readbackFailed = false;
    this.saving = false;
    this.emitDirtyState(false);
  }

  private onWindowDaysChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = Number(input.value);
    if (!Number.isSafeInteger(value) || value < 1) {
      this.error = t('workspaceSyncRulesInvalidWindow') as string;
      return;
    }
    this.setDraft({ ...this.draft, windowDays: value });
  }

  private onExcludeAllDayChange(event: Event) {
    this.setDraft({ ...this.draft, excludeAllDay: (event.target as HTMLInputElement).checked });
  }

  private onNoDueDateChange(event: Event) {
    this.setDraft({ ...this.draft, noDueDate: (event.target as HTMLSelectElement).value as NoDueDateMode });
  }

  private async save() {
    if (!this.isOwner || this.saving || this.loading || !this.dirty) return;
    if (!Number.isSafeInteger(this.draft.windowDays) || this.draft.windowDays < 1) {
      this.error = t('workspaceSyncRulesInvalidWindow') as string;
      return;
    }

    const generation = ++this.requestGeneration;
    const attemptDraft = { ...this.draft };
    this.saving = true;
    this.error = '';
    this.success = '';
    this.readbackFailed = false;
    try {
      await api('/api/workspaces/me/settings', {
        method: 'PATCH',
        json: workspaceSyncRulesPatch(this.draft),
      });

      try {
        const readback = await api('/api/workspaces/me');
        if (!this.isCurrent(generation)) return;
        const next = normalizeWorkspaceSyncRules(this.extractSyncRules(readback));
        const editedDuringSave = !workspaceSyncRulesEqual(this.draft, attemptDraft);
        this.baseline = next;
        // A readback reflects the submitted snapshot, not edits made while the
        // request was in flight. Keep those later edits dirty for the next save.
        this.draft = editedDuringSave ? this.draft : { ...next };
        this.success = t('workspaceSyncRulesSaveSuccess') as string;
        this.emitDirtyState(editedDuringSave);
      } catch {
        if (!this.isCurrent(generation)) return;
        // PATCH succeeded, but do not pretend the persisted value was read back.
        // Keep the draft and dirty state so the user can explicitly retry.
        this.success = t('workspaceSyncRulesSaveSuccess') as string;
        this.error = t('workspaceSyncRulesReadbackError') as string;
        this.readbackFailed = true;
      }
    } catch {
      if (!this.isCurrent(generation)) return;
      this.error = errorMessage();
    } finally {
      if (this.isCurrent(generation)) this.saving = false;
    }
  }

  private renderReadOnlyValue(label: string, value: string) {
    return html`
      <div class="workspace-sync-rules-readonly-row">
        <span class="workspace-sync-rules-label">${label}</span>
        <span class="workspace-sync-rules-value">${value}</span>
      </div>
    `;
  }

  private renderCalendarSettings() {
    if (!this.isOwner) {
      return html`
        ${this.renderReadOnlyValue(t('workspaceSyncRulesWindowLabel') as string, `${this.draft.windowDays} ${t('workspaceSyncRulesDays')}`)}
        ${this.renderReadOnlyValue(t('workspaceSyncRulesExcludeAllDayLabel') as string, this.draft.excludeAllDay
          ? t('workspaceSyncRulesExcludeAllDayOn') as string : t('workspaceSyncRulesExcludeAllDayOff') as string)}
      `;
    }

    return html`
      <label class="workspace-sync-rules-field">
        <span class="workspace-sync-rules-label">${t('workspaceSyncRulesWindowLabel')}</span>
        <span class="workspace-sync-rules-control-row">
          <input
            class="workspace-sync-rules-window-input"
            type="number"
            min="1"
            step="1"
            inputmode="numeric"
            aria-label=${t('workspaceSyncRulesWindowLabel') as string}
            .value=${String(this.draft.windowDays)}
            @change=${this.onWindowDaysChange}
          >
          <span>${t('workspaceSyncRulesDays')}</span>
        </span>
        <small class="field-hint">${t('workspaceSyncRulesWindowHint')}</small>
      </label>
      <label class="workspace-sync-rules-checkbox">
        <input type="checkbox" .checked=${this.draft.excludeAllDay} @change=${this.onExcludeAllDayChange}>
        <span>${t('workspaceSyncRulesExcludeAllDayLabel')}</span>
      </label>
      <small class="field-hint">${t('workspaceSyncRulesExcludeAllDayHint')}</small>
    `;
  }

  private renderNoDueDateSettings() {
    if (!this.isOwner) {
      return this.renderReadOnlyValue(
        t('workspaceSyncRulesNoDueDateLabel') as string,
        t(`workspaceSyncRulesNoDueDate_${this.draft.noDueDate}`) as string,
      );
    }

    return html`
      <label class="workspace-sync-rules-field">
        <span class="workspace-sync-rules-label">${t('workspaceSyncRulesNoDueDateLabel')}</span>
        <select
          class="workspace-sync-rules-select"
          aria-label=${t('workspaceSyncRulesNoDueDateLabel') as string}
          .value=${this.draft.noDueDate}
          @change=${this.onNoDueDateChange}
        >
          <option value="keep_empty">${t('workspaceSyncRulesNoDueDate_keep_empty')}</option>
          <option value="propose_estimate">${t('workspaceSyncRulesNoDueDate_propose_estimate')}</option>
          <option value="create_decide_task">${t('workspaceSyncRulesNoDueDate_create_decide_task')}</option>
        </select>
        <small class="field-hint">${t('workspaceSyncRulesNoDueDateHint')}</small>
      </label>
    `;
  }

  render() {
    if (this.loading) return html`<div class="settings-section-placeholder">${t('workspaceSyncRulesLoading')}</div>`;

    return html`
      <div class="group-note">
        <span class="mi" aria-hidden="true">info</span>
        <span>${t('workspaceSyncRulesNote')}</span>
      </div>
      ${!this.isOwner ? html`
        <div class="settings-alert" role="note">
          <span class="mi" aria-hidden="true">lock</span>
          <span>${t('workspaceSyncRulesOwnerOnlyNote')}</span>
        </div>
      ` : nothing}
      ${this.error ? html`<div class="msg msg-error show" role="alert">${this.error}</div>` : nothing}
      ${this.success ? html`<div class="msg msg-success show" role="status" aria-live="polite">${this.success}</div>` : nothing}
      ${this.readbackFailed ? html`
        <button type="button" class="btn btn-secondary workspace-sync-rules-retry" @click=${this.retryLoad} ?disabled=${this.saving}>
          <span class="mi mi-sm" aria-hidden="true">refresh</span>${t('workspaceSyncRulesReload')}
        </button>
      ` : nothing}

      ${!this.loaded && this.error ? html`
        <button type="button" class="btn btn-secondary workspace-sync-rules-retry" @click=${this.retryLoad} ?disabled=${this.saving}>
          <span class="mi mi-sm" aria-hidden="true">refresh</span>${t('workspaceSyncRulesReload')}
        </button>
      ` : nothing}

      ${this.loaded ? html`<section class="workspace-sync-rules-block" aria-labelledby="workspaceSyncRulesCalendarHeading">
        <h4 id="workspaceSyncRulesCalendarHeading"><span class="mi mi-sm" aria-hidden="true">calendar_month</span>${t('workspaceSyncRulesCalendarHeading')}</h4>
        <p class="field-hint">${t('workspaceSyncRulesCalendarDescription')}</p>
        <div class="workspace-sync-rules-fields">${this.renderCalendarSettings()}</div>
        <p class="field-hint workspace-sync-rules-connection-link">
          <span class="mi mi-xs" aria-hidden="true">key</span>
          <a href="#settings-my-connections">${t('workspaceSyncRulesCalendarConnectionLink')}</a>
        </p>
      </section>` : nothing}

      ${this.loaded ? html`<section class="workspace-sync-rules-block" aria-labelledby="workspaceSyncRulesGoalHeading">
        <h4 id="workspaceSyncRulesGoalHeading"><span class="mi mi-sm" aria-hidden="true">event_note</span>${t('workspaceSyncRulesGoalHeading')}</h4>
        <p class="field-hint">${t('workspaceSyncRulesGoalDescription')}</p>
        <div class="workspace-sync-rules-fields">${this.renderNoDueDateSettings()}</div>
      </section>` : nothing}

      ${this.loaded && this.isOwner ? html`
        <div class="workspace-sync-rules-actions">
          <button type="button" class="btn btn-primary" @click=${this.save} ?disabled=${this.saving || !this.dirty}>
            ${this.saving ? html`<span class="spinner spinner-white"></span>${t('workspaceSyncRulesSaving')}` : html`<span class="mi mi-sm" aria-hidden="true">save</span>${t('workspaceSyncRulesSave')}`}
          </button>
          ${this.dirty ? html`<span class="field-hint" role="status">${t('workspaceSyncRulesUnsaved')}</span>` : nothing}
        </div>
      ` : nothing}
    `;
  }
}

defineZenComponent('zen-workspace-sync-rules-section', ZenWorkspaceSyncRulesSection);
